import React, { FC, useState } from 'react';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import TimesheetRepository from 'src/api/repositories/ess/v1/timesheet.repository';
import { TimesheetStatus } from 'src/features/timesheets';
import { getRoute } from 'src/routes';
import { DescriptionIcon, EmptyState, HorizontalBarChart, ServerError500Icon, Tab, Tabs } from 'src/shared/components';
import { API_DATE_FORMAT, DATE_MONTH_FORMAT } from 'src/shared/constants';
import { DurationFormatEnums, PermissionsEnums, TimesheetSummaryTypeEnums } from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useTranslate } from 'src/shared/providers';
import { formatDuration, mapHorizontalBarDarkColors, mapHorizontalBarLightColors } from 'src/shared/utils';

export const TimesheetCard: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslate();
  const theme = useTheme();

  const mapper = theme.palette.mode === 'light' ? mapHorizontalBarLightColors : mapHorizontalBarDarkColors;

  const [type, setType] = useState(TimesheetSummaryTypeEnums.WEEKLY);

  const {
    data: timesheetDashboardSummary,
    isLoading,
    isFetching,
    error,
  } = TimesheetRepository.endpoints.useGetTimesheetDashboardSummaryQuery({
    date: moment().format(API_DATE_FORMAT),
    summaryType: type,
  });

  const loading = isLoading || isFetching;

  const handleTabChange = (tab: TimesheetSummaryTypeEnums) => {
    setType(tab);
  };

  const handleNavigateToTimesheet = () => {
    navigate(getRoute('timesheets'));
  };

  if (!timesheetDashboardSummary) {
    return <Skeleton className="rounded border" variant="rectangular" animation="wave" height={320} />;
  }

  return (
    <Paper className="h-100 p-4 overflow-hidden">
      <Stack direction="column" gap={2} height="100%">
        <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
          <Stack direction="row" gap={1} alignItems="center">
            <h4 className="pb-0 mb-0 me-1">{t('timesheets.timesheet')}</h4>
            <TimesheetStatus status={timesheetDashboardSummary.status} />
          </Stack>
          {/*TODO update <a> to real nav link*/}
          <Button
            color="secondary"
            startIcon={<DescriptionIcon size={20} />}
            sx={{ height: 40, fontSize: '0.875rem', color: (theme) => `${theme.palette.primary.main} !important` }}
            onClick={handleNavigateToTimesheet}
          >
            {t('timesheets.goToTimesheet')}
          </Button>
        </Stack>
        {!!error && (
          <EmptyState className="m-0" message={t('global.oopsAnErrorOccurred')} icon={<ServerError500Icon />} />
        )}
        {!error && (
          <>
            <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
              <Tabs value={type} onClick={handleTabChange}>
                <Tab value={TimesheetSummaryTypeEnums.WEEKLY} label={t('timesheets.weeklyTotals')} />
                <Tab value={TimesheetSummaryTypeEnums.PAY_PERIOD} label={t('timesheets.payPeriod')} />
              </Tabs>
              <Typography fontSize="0.875rem" color="text.secondary">
                {moment(timesheetDashboardSummary.startDate).format(DATE_MONTH_FORMAT)}
                {' - '}
                {moment(timesheetDashboardSummary.endDate).format(DATE_MONTH_FORMAT)}
              </Typography>
            </Stack>
            {!loading ? (
              <>
                <Stack direction="row" gap={2} alignItems="center">
                  <Typography fontSize="2.125rem" fontWeight={500}>
                    {formatDuration(timesheetDashboardSummary.hoursTotal, DurationFormatEnums.T_24_HOURS, 'hours')}
                  </Typography>
                  <Typography
                    fontSize="0.75rem"
                    color="text.secondary"
                    whiteSpace="pre-wrap"
                    sx={{ width: 'min-content' }}
                  >
                    {t('timesheets.totalHours')}
                  </Typography>
                </Stack>
                <Stack>
                  {timesheetDashboardSummary.hours.map((amount, index) => (
                    // TODO Define color mapper
                    <HorizontalBarChart
                      key={amount.id}
                      color={mapper[index % mapper.length]}
                      variant={amount.hours ? 'dark' : 'light'}
                      value={getValue(amount.hours, timesheetDashboardSummary.hoursTotal)}
                      label={amount.description ?? undefined}
                      extraLabel={
                        amount.hours ? formatDuration(amount.hours, DurationFormatEnums.T_24_HOURS, 'hours') : undefined
                      }
                    />
                  ))}
                </Stack>
                <ComponentWithPermissions permissions={PermissionsEnums.WTS_INPUT_EXPENSE}>
                  <Divider />
                  <Typography fontSize="1.5rem" fontWeight="bold">
                    {t('timesheets.expenses')} ($)
                  </Typography>
                  <Stack direction="row" gap={2} justifyContent="space-between">
                    <Stack direction="row" gap={2} alignItems="center">
                      <Typography fontSize="2.125rem" fontWeight={500}>
                        {(timesheetDashboardSummary.expenseTotal ?? 0).toFixed(2)}
                      </Typography>
                      <Typography
                        fontSize="0.75rem"
                        color="text.secondary"
                        whiteSpace="pre-wrap"
                        sx={{ width: 'min-content' }}
                      >
                        {t('timesheets.totalExpenses')}
                      </Typography>
                    </Stack>
                    <Typography fontSize="0.75rem" fontWeight="bold" color="text.secondary">
                      {t('global.currentWeek')}
                    </Typography>
                  </Stack>
                  <Stack width="fit-content" gap={2}>
                    {timesheetDashboardSummary.expenses?.map((expense) => (
                      <Stack
                        key={expense.id}
                        direction="row"
                        gap={2}
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                          borderRadius: '10px',
                          padding: 2,
                        }}
                      >
                        <Typography fontSize="0.875rem" fontWeight={600} color="text.secondary">
                          {expense.description}
                        </Typography>
                        <Typography fontSize="0.875rem" fontWeight={600} color={(theme) => theme.palette.primary.main}>
                          {expense.memoAmount.toFixed(2)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </ComponentWithPermissions>
              </>
            ) : (
              <Skeleton className="rounded" variant="rectangular" animation="wave" sx={{ height: '100%' }} />
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
};

const getValue = (duration: number, total: number) => (duration / total) * 100;
